import React, { useState, useEffect, useRef } from 'react'

import { connect } from 'containers/store'
import { sharedActionCreators } from 'containers/shared'

import { ButtonGroup } from 'components/ui/button'
import { FooterEditEntity } from 'components/ui/footer'
import { PrimaryNavigationEditEntity } from 'components/ui/primary-navigation/edit-entity'
import { PopOverMenu, PopOverMenuButton } from 'components/ui/pop-over-menu'
import { SecondaryNavigation } from 'components/ui/secondary-navigation'
import { StatusGuard } from 'components/ui/status-guard'

import { releaseControlsBindableActionCreators } from 'domains/release-controls/actions'
import { ReleaseControlUpdate } from 'domains/release-controls/components/release-control-update'

import { AppPageUpdate } from './app-page-update'
import { ContentPageUpdate } from './content-page-update'
import { DeepDivePageUpdate } from './deep-dive-page-update'
import { HTMLPageUpdate } from './html-page-update'
import { ImagePageUpdate } from './image-page-update'
import { URLPageUpdate } from './url-page-update'

import { SetReleaseStatusRequest } from 'generated/mos/releasecontrolsmanagement'
import { VisitorInfoPage } from 'generated/mos/org/getty/visitorinfo'
import { GetVisitorInfoPageRequest } from 'generated/mos/org/getty/visitorinfoaccess'

import { assertNever, pick } from 'helpers/core'
import { statusSelector, Status } from 'helpers/status'

import { LayoutListOrFormPage } from 'layouts/list-or-form-page'

import { actionCreators, GettyActionCreators } from '../../../actions'

import { VisitorInfoDelete } from '../visitor-info-delete'

import { getPageSubnav } from '../subnav'

import { GettyStore } from '../../../store'


import { visitorInfoSchema } from '../../../schemas/visitorInfo'

type ActionProps =
  Pick<GettyActionCreators, 'gettyGetVisitorInfoPageRequest'> &
  Pick<typeof releaseControlsBindableActionCreators, "releaseControlsListReleaseControlsRequest" | "releaseControlsSetReleaseStatusRequest"> &
  Pick<typeof sharedActionCreators, 'toastNotification'>

type ConnectedProps = Pick<GettyStore['getty'], 'visitorInfoUpdate'>

type DirectProps = {
  readonly historyPush: (id: string) => void;
  readonly projectId: string;
  readonly refId: string;
}

type Props = ActionProps & ConnectedProps & DirectProps


const VisitorInfoUpdateView = (props: Props) => {
  const {
    visitorInfoUpdate,
    gettyGetVisitorInfoPageRequest,
    historyPush,
    projectId,
    refId,
    toastNotification,
    releaseControlsListReleaseControlsRequest,
    releaseControlsSetReleaseStatusRequest,
  } = props

  const page = statusSelector.data(visitorInfoUpdate)

  const [submit, setSubmit] = useState<() => void>(() => {})
  const [hasSubmitted, setHasSubmitted] = useState<boolean>(false)

  const [modified, setModified] = useState<boolean | undefined>(false)
  const [deletePageModalOpen, setDeletePageModalOpen] = useState(false)

  const renderView = (pageType: VisitorInfoPage.Entity['pageProperties']) => {
    const pageId = page && page.ref && page.ref.id || "";
    const viewProps = { page, setSubmit, setModified, setHasSubmitted, pageId}
    const type = pageType && pageType.type
    if(!type) return null
    switch(type) {
      case VisitorInfoPage.SiteMap.refName :
        return <ImagePageUpdate {...viewProps} />
      case VisitorInfoPage.StaticWebPage.refName :
        return <HTMLPageUpdate {...viewProps} />
      case VisitorInfoPage.URLWebPage.refName :
        return <URLPageUpdate {...viewProps} />
      case VisitorInfoPage.AppPage.refName :
        return <AppPageUpdate {...viewProps} />
      case VisitorInfoPage.AmenityCategory.refName :
        return <ContentPageUpdate {...viewProps} />
      case VisitorInfoPage.DeepDivePage.refName :
        return <DeepDivePageUpdate {...viewProps} />
      case VisitorInfoPage.SectionSeparator.refName :
        return null
      default:
        return assertNever(type)
    }
  }

  // load initial data
  useEffect(() => {
    if (visitorInfoUpdate.status === Status.Idle || page && VisitorInfoPage.mustRef(page.ref).id !== refId) {
      gettyGetVisitorInfoPageRequest({
        type: GetVisitorInfoPageRequest.refName,
        ref: {
          typename: VisitorInfoPage.refName,
          id: refId
        }
      })
      releaseControlsListReleaseControlsRequest([{
        typename: VisitorInfoPage.refName,
        id: refId,
      }])
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // if the status is ready and the data had been submitted, trigger success toast.
    if (visitorInfoUpdate.status === Status.Ready && hasSubmitted) {
      releaseControlsListReleaseControlsRequest([{
        typename: VisitorInfoPage.refName,
        id: refId,
      }])
      toastNotification({ type: 'success', text: 'Visitor info Page updated' })
      setHasSubmitted(false)
    }
  }, [ visitorInfoUpdate.status, hasSubmitted, toastNotification ]) // eslint-disable-line react-hooks/exhaustive-deps

  const backLocation = `/project/${projectId}/visitor-info`

  const releaseControlUpdate = (bundles: ReadonlyArray<SetReleaseStatusRequest.BundleStatus.Entity>) => {
    page && releaseControlsSetReleaseStatusRequest({
      ...SetReleaseStatusRequest.defaults,
      ref: page.ref,
      bundles,
    })
  }

  return (
    <>
      {deletePageModalOpen && page && (
        <VisitorInfoDelete onCancel={() => setDeletePageModalOpen(false)} page={page} onComplete={() => historyPush(backLocation)} />
      )}
      <LayoutListOrFormPage
        nav={() => (
          <PrimaryNavigationEditEntity
            title="Edit page"
            onConfirm={modified ? () => historyPush(backLocation) : undefined}
            navBackRoute={backLocation}
          />
        )}
        title={page && page.listTitle && visitorInfoSchema.listTitle ? visitorInfoSchema.listTitle.read(page.listTitle) as string : '' }
        toolbar={() => (
          <ButtonGroup>
            {page &&
              <ReleaseControlUpdate
                hasBorder
                entityRef={page.ref}
                updateHandler={releaseControlUpdate}
              />
            }
            <PopOverMenu hasBorder={true}>
              <PopOverMenuButton onClick={() => setDeletePageModalOpen(true)}>
                Delete
              </PopOverMenuButton>
            </PopOverMenu>
          </ButtonGroup>
        )}
        subnav={() => (
          <SecondaryNavigation
            navItems={getPageSubnav(projectId, refId)}
            modified={modified}
            historyPush={historyPush}
          />
        )}
        content={() => (
          <StatusGuard status={visitorInfoUpdate} mode="form">
            {page && page.pageProperties ? renderView(page.pageProperties) : null}
          </StatusGuard>
        )}
        sidebar={() => null}
        footer={() => (
          <FooterEditEntity
            entity={visitorInfoUpdate}
            modified={modified}
            onCancel={() => historyPush(backLocation)}
            onSave={() => {
              submit()
              return false
            }}
          />
        )}
      />
    </>
  )
}

export const VisitorInfoUpdate = connect<ConnectedProps, ActionProps, DirectProps, GettyStore, GettyActionCreators>(
  store => ({
    ...pick(store.getty, 'visitorInfoUpdate'),
  }),
  {
    ...pick(actionCreators, 'gettyGetVisitorInfoPageRequest'),
    ...pick(releaseControlsBindableActionCreators, 'releaseControlsListReleaseControlsRequest', 'releaseControlsSetReleaseStatusRequest'),
    ...pick(sharedActionCreators, 'toastNotification'),
  },
)(VisitorInfoUpdateView)
